import React, { memo, useCallback, useMemo } from 'react';
import { bool, oneOf } from 'prop-types';
import classNames from 'classnames';
import { Favourite } from '@johnlewispartnership/wtr-style-dictionary/icons/react/Actions';
import { Account } from '@johnlewispartnership/wtr-style-dictionary/icons/react/Account';
import { Document } from '@johnlewispartnership/wtr-style-dictionary/icons/react/Documents';
import { useAnalytics } from '@johnlewispartnership/wtr-website-analytics';
import { urls } from 'constants/urls';
import AnchorLink from 'components/AnchorLink';
import { handleLoginWithRedirect } from 'utils/login';

import ShoppingSummaryContainer from '../ShoppingSummary';

import MyAccount from './MyAccount/MyAccount';

import styles from './LinksBar.scss';
import SlotButton from '../SlotButton';

const LinksBar = memo(({ isLoggedIn, viewport, scrolled, showMinSpend }) => {
  const { trackEvent } = useAnalytics();

  const { isXXSMobile, isMobile, isTabletBreakpoint, isXLDesktop } = useMemo(
    () => ({
      isXXSMobile: viewport === 'xxs-mobile',
      isMobile: viewport === 'xxs-mobile' || viewport === 'xs-mobile' || viewport === 'mobile',
      isTabletBreakpoint: viewport === 'tablet',
      isXLDesktop: viewport === 'xl-desktop',
    }),
    [viewport],
  );

  const pushMenuClick = useCallback(
    buttonLabel => {
      trackEvent({
        event: 'click_megamenu',
        mega_menu: {
          button_clicked: buttonLabel,
        },
      });
    },
    [trackEvent],
  );

  const unauthenticatedAccountLinks = (
    <div aria-label="Sign in and register">
      <AnchorLink
        className={styles.links}
        href={urls.login}
        onClick={handleLoginWithRedirect}
        underline="never"
      >
        <Account size="small" />
        <span className={classNames(styles.anchorText, styles.signInText)} data-testid="signIn">
          Sign in/Register
        </span>
      </AnchorLink>
    </div>
  );

  const authenticatedAccountLinks = isXXSMobile ? null : (
    <div aria-label="Account links" className={styles.authenticatedLinks}>
      {!isTabletBreakpoint && (
        <AnchorLink
          id="site-header-favourites"
          to={urls.favourites}
          className={styles.links}
          onClick={() => pushMenuClick('Favourites')}
        >
          <Favourite className={styles.favouritesIcon} size="small" />
          <span
            className={classNames(styles.anchorText, styles.favouritesText, {
              [styles.hasMinSpend]: showMinSpend,
            })}
          >
            Favourites
          </span>
        </AnchorLink>
      )}
      {isXLDesktop && (
        <AnchorLink
          id="site-header-orders"
          to={urls.myOrdersPage}
          className={styles.links}
          onClick={() => pushMenuClick('Orders')}
        >
          <Document className={styles.favouritesIcon} size="small" />
          <span className={styles.anchorText}>Orders</span>
        </AnchorLink>
      )}
      {!isMobile && <MyAccount />}
    </div>
  );

  const accountLinks = isLoggedIn ? authenticatedAccountLinks : unauthenticatedAccountLinks;

  return (
    <div className={styles.linksBar}>
      {accountLinks}
      <SlotButton />
      {(!isMobile && !isTabletBreakpoint && !isLoggedIn) || isLoggedIn ? (
        <ShoppingSummaryContainer scrolled={scrolled} />
      ) : null}
    </div>
  );
});

LinksBar.displayName = 'LinksBar';

LinksBar.propTypes = {
  viewport: oneOf(['xxs-mobile', 'xs-mobile', 'mobile', 'tablet', 'desktop', 'xl-desktop'])
    .isRequired,
  isLoggedIn: bool,
  scrolled: bool,
  showMinSpend: bool,
};

LinksBar.defaultProps = {
  isLoggedIn: false,
  scrolled: false,
  showMinSpend: false,
};

export default LinksBar;
